import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import { Button, Card, Dialog, Stack, Typography, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../utils/formatUtils';
import WalletSelector from '../../../features/Transactions/WalletSelector';
import IconCircleWrapper from '../../../ui/IconCircleWrapper';
import { CaretRightIcon, ExpandCircleRightIcon, HomeIcon } from '../../../ui/icons';
import DynamicSvgIcon from '../../../ui/icons/DynamicSvgIcon';
import DatePickerComponent from './DatePickerComponent';
import DescriptionField from './DescriptionField';
import ReAllocateMoney from './ReAllocateMoney';
import { Category } from './types';

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface Participant {
  id: string;
  name: string;
  amount?: number;
  portions?: number;
}

export interface WalletWithAllocateAmount {
  wallet: Wallet;
  allocateAmount: number;
}

interface LendTransactionFormProps {
  amount: number | null;
  setLendWallets: (wallet: WalletWithAllocateAmount[] | null) => void;
  setExpenseWallet: (wallet: WalletWithAllocateAmount | null) => void;
  setSourceWallet: (wallet: Wallet | null) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  setDescription: (description: string) => void;
  setOpenDialogSelectedExpenseCategory: (open: boolean) => void;
  spendingCategory: Category | null;
  setSpendingCategory: (category: Category | null) => void;
  readOnly?: boolean;
  // Initial values for editing
  initialLendWallets?: WalletWithAllocateAmount[] | null;
  initialExpenseWallet?: WalletWithAllocateAmount | null;
  initialSourceWallet?: Wallet | null;
  initialDatePicker?: Dayjs | undefined;
  initialDescription?: string;
  initialSpendingCategory?: Category | null;
  isUpdate?: boolean;
}

type Step = {
  condition: () => boolean;
  action: () => void;
};

const LendTransactionForm = (props: LendTransactionFormProps) => {
  const {
    amount,
    spendingCategory,
    setSpendingCategory,
    setExpenseWallet,
    setLendWallets: setLendWalletsProp,
    setSourceWallet: setSourceWalletProp,
    setDatePicker: setDatePickerProp,
    setDescription: setDescriptionProp,
    setOpenDialogSelectedExpenseCategory,
    readOnly = false,
    initialLendWallets,
    // initialExpenseWallet, // TODO: Handle expense wallet initialization for editing
    initialSourceWallet,
    initialDatePicker,
    initialDescription,
    initialSpendingCategory,
    isUpdate
  } = props;
  const theme = useTheme();
  const [firstRender, setFirstRender] = useState(true);
  const { t } = useTranslation(['common', 'transactions', 'loan']);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const [lendWallets, setLendWallets] = useState<Wallet[]>(
    initialLendWallets?.map(w => w.wallet) || []
  );
  const [sourceWallet, setSourceWallet] = useState<Wallet | null>(initialSourceWallet || null);
  const [datePicker, setDatePicker] = useState<Dayjs | undefined>(initialDatePicker || dayjs());
  const [description, setDescription] = useState<string>(initialDescription || '');
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [openDialogSelectLendWallets, setOpenDialogSelectLendWallets] =
    useState<boolean>(false);
  const [openDialogSelectSourceWallets, setOpenDialogSelectSourceWallets] =
    useState<boolean>(false);
  const [openReAllocateMoneyDialog, setOpenReAllocateMoneyDialog] = useState<boolean>(false);

  // Check if personal wallet (Tôi) is selected
  const hasPersonalWallet = lendWallets.some(
    (wallet) => wallet.wallet.id === 'personal-wallet'
  );

  const stepsConfig: Step[] = [
    {
      condition: () => !!amount,
      action: () => null,
    },
    {
      condition: () => !!lendWallets.length,
      action: () => setOpenDialogSelectLendWallets(true),
    },
    {
      // If personal wallet is selected, require spending category
      condition: () => !hasPersonalWallet || !!spendingCategory,
      action: () => setOpenDialogSelectedExpenseCategory(true),
    },
    {
      condition: () => !!sourceWallet,
      action: () => setOpenDialogSelectSourceWallets(true),
    },
    {
      condition: () => !!datePicker,
      action: () => setOpenDatePicker(true),
    },
    {
      condition: () => !!description,
      action: () => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.click();
      },
    },
  ];

  const handleOnCloseDialog = () => {
    setOpenDialogSelectLendWallets(false);
    setOpenDialogSelectSourceWallets(false);
    setOpenReAllocateMoneyDialog(false);
  };

  const [allocateAmountMap, setAllocateAmountMap] = useState<Map<string, number>>(
    new Map<string, number>(
      initialLendWallets?.map(w => [w.wallet.wallet.id, w.allocateAmount]) || []
    )
  );
  const [allocatePortionsMap, setAllocatePortionsMap] = useState<Map<string, number>>(
    new Map<string, number>(
      initialLendWallets?.map(w => [w.wallet.wallet.id, 1]) || []
    )
  );

  // Initialize spending category from initial value
  useEffect(() => {
    if (initialSpendingCategory && !spendingCategory) {
      setSpendingCategory(initialSpendingCategory);
    }
  }, [initialSpendingCategory, spendingCategory, setSpendingCategory]);


  // Update internal state when initial values change
  useEffect(() => {
    if (initialSourceWallet !== undefined) {
      setSourceWallet(initialSourceWallet);
      setSourceWalletProp(initialSourceWallet);
    }
  }, [initialSourceWallet, setSourceWalletProp]);

  useEffect(() => {
    if (initialDatePicker !== undefined) {
      setDatePicker(initialDatePicker);
      setDatePickerProp(initialDatePicker);
    }
  }, [initialDatePicker, setDatePickerProp]);

  useEffect(() => {
    if (initialDescription !== undefined) {
      setDescription(initialDescription);
      setDescriptionProp(initialDescription);
    }
  }, [initialDescription, setDescriptionProp]);


  useEffect(() => {
    if (initialLendWallets !== undefined && initialLendWallets !== null) {
      const wallets = initialLendWallets.map(w => w.wallet);
      setLendWallets(wallets);

      // Initialize allocate amount map from initial data
      const newAllocateAmountMap = new Map<string, number>();
      const newAllocatePortionsMap = new Map<string, number>();

      initialLendWallets.forEach(w => {
        newAllocateAmountMap.set(w.wallet.wallet.id, w.allocateAmount);
        newAllocatePortionsMap.set(w.wallet.wallet.id, 1);
      });

      setAllocateAmountMap(newAllocateAmountMap);
      setAllocatePortionsMap(newAllocatePortionsMap);
    }
  }, [initialLendWallets]);

  // watch props amount change to update all lendWallets allocateAmount
  useEffect(() => {
    if (amount && lendWallets.length > 0) {
      const totalAmount = amount || 0;
      const walletCount = lendWallets.length;
      const baseAmount = Math.floor(totalAmount / walletCount);
      const remainder = totalAmount % walletCount;

      const newAllocateAmountMap = new Map<string, number>();
      const newAllocatePortionsMap = new Map<string, number>();

      // Distribute base amount to all wallets
      lendWallets.forEach((wallet, index) => {
        // Add all remainder to the first wallet only
        const extraAmount = index === 0 ? remainder : 0;
        newAllocateAmountMap.set(wallet.wallet.id, baseAmount + extraAmount);

        // Initialize with 1 portion per wallet by default
        newAllocatePortionsMap.set(wallet.wallet.id, 1);
      });

      setAllocateAmountMap(newAllocateAmountMap);
      setAllocatePortionsMap(newAllocatePortionsMap);
    } else {
      setAllocateAmountMap(new Map<string, number>());
      setAllocatePortionsMap(new Map<string, number>());
    }
  }, [amount, lendWallets]);

  const handleReAllocateComplete = (allocations: Participant[]) => {
    console.log("🚀 ~ handleReAllocateComplete ~ allocations:", allocations)

    // Create new Maps to store the reallocated amounts and portions
    const newAllocateAmountMap = new Map<string, number>();
    const newAllocatePortionsMap = new Map<string, number>();

    // Extract the amounts and portions from the allocations and update the maps
    allocations.forEach(allocation => {
      if (allocation.amount !== undefined) {
        newAllocateAmountMap.set(allocation.id, allocation.amount);
      }

      if (allocation.portions !== undefined) {
        newAllocatePortionsMap.set(allocation.id, allocation.portions);
      }
    });
    setAllocateAmountMap(newAllocateAmountMap);

    setAllocatePortionsMap(newAllocatePortionsMap);
    setOpenReAllocateMoneyDialog(false);
  };

  // watch lendWallets and allocateAmountMap changes to update setLendWalletsProp
  useEffect(() => {
    if (lendWallets.length > 0) {
      const updatedLendWallets = lendWallets.map((wallet) => ({
        wallet,
        allocateAmount: allocateAmountMap.get(wallet.wallet.id) || 0,
      }));
      const personalWallet = lendWallets.find(
        (wallet) => wallet.wallet.id === 'personal-wallet'
      );

      // ALWAYS filter out personal-wallet from lending wallets
      // Personal wallet share is handled as expense, not loan
      const filteredLendWallets = updatedLendWallets.filter(
        (w) => w.wallet.id !== 'personal-wallet'
      );
      setLendWalletsProp(filteredLendWallets);

      // Set expense wallet for personal share (requires sourceWallet)
      if (personalWallet && sourceWallet) {
        setExpenseWallet({
          wallet: sourceWallet,
          allocateAmount: allocateAmountMap.get(personalWallet.wallet.id) || 0,
        });
      } else {
        setExpenseWallet(null);
      }
    } else {
      // No wallets selected - clear both
      setLendWalletsProp([]);
      setExpenseWallet(null);
    }
  }, [
    lendWallets,
    allocateAmountMap,
    setExpenseWallet,
    setLendWalletsProp,
    sourceWallet,
    readOnly,
  ]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    }

    if (isUpdate) return;

    for (const step of stepsConfig) {
      if (!step.condition()) {
        step.action();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, spendingCategory, lendWallets, sourceWallet, datePicker, firstRender, hasPersonalWallet]);

  return (
    <>
      <Stack spacing={4} width="100%">
        {/* Lend Wallet */}
        <Stack direction="column">
          <Stack
            direction="row"
            spacing={theme.spacing(2)}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={theme.spacing(2)}
              alignItems="center"
            >
              <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
                <PersonIcon sx={{ fontSize: 18 }} />
              </IconCircleWrapper>
            </Stack>
            <Stack
              borderBottom={
                lendWallets?.length > 0
                  ? 'none'
                  : `1px solid ${theme.palette.divider}`
              }
              width="100%"
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              paddingBottom={theme.spacing(2)}
              onClick={readOnly ? undefined : () => setOpenDialogSelectLendWallets(true)}
              sx={{ cursor: readOnly ? 'default' : 'pointer' }}
            >
              <Typography variant="body1" color="secondary">
                {t('transaction_form.lending_destination_wallet', {
                  ns: 'loan',
                })}
              </Typography>
              {lendWallets?.length > 0 ? (
                <ExpandCircleRightIcon
                  style={{
                    fontSize: 24,
                    color: theme.palette.primary.main,
                  }}
                />
              ) : (
                <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
              )}
            </Stack>
          </Stack>
          {lendWallets.length > 0 && (
            <>
              <Stack
                paddingLeft={theme.spacing(3)} // Align with the content above
              >
                {lendWallets.map((item, index) => (
                  <Stack
                    key={item.wallet.id} // Use wallet.id instead of index for better React key
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    padding={theme.spacing(1)}
                  >
                    <Typography
                      variant="h3"
                      color="textPrimary"
                      fontWeight={400}
                    >
                      {item.wallet.name}
                    </Typography>
                    <Typography
                      variant="h3"
                      color="textPrimary"
                      fontWeight={600}
                    >
                      {formatCurrency(
                        allocateAmountMap.get(item.wallet.id) || 0
                      )}
                    </Typography>
                  </Stack>
                ))}
                {
                  lendWallets.length > 1 ? (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      paddingRight={theme.spacing(1)}
                      paddingTop={theme.spacing(1)}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => setOpenReAllocateMoneyDialog(true)}
                      >
                        {t('transaction_form.reallocate_money', { ns: 'loan' })}
                      </Button>
                    </Stack>
                  ) : null
                }
              </Stack>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.spacing(2),
                }}
              >
                <div style={{ width: 24 }} />
                <div
                  style={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    width: '100%',
                    paddingBottom: theme.spacing(2),
                  }}
                />
              </div>
            </>
          )}
        </Stack>

        {/* Spending Category - Only show when personal wallet (Tôi) is selected */}
        {hasPersonalWallet && (
          <Stack
            direction="row"
            spacing={theme.spacing(2)}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
              {spendingCategory?.icon ? (
                <Card elevation={0} sx={{ borderRadius: '50%', overflow: 'hidden', display: 'inline-block' }}>
                  <DynamicSvgIcon
                    svgUrl={spendingCategory.icon}
                    color={spendingCategory.iconColor}
                    backgroundColor={spendingCategory.backgroundColor}
                    size={30}
                    iconSize={18}
                  />
                </Card>
              ) : (
                <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
                  <HomeIcon />
                </IconCircleWrapper>
              )}
            </Stack>
            <Stack
              borderBottom={`1px solid ${theme.palette.divider}`}
              width="100%"
              alignItems="end"
              direction="row"
              justifyContent="space-between"
              paddingBottom={theme.spacing(2)}
              onClick={readOnly ? undefined : () => setOpenDialogSelectedExpenseCategory(true)}
              sx={{ cursor: readOnly ? 'default' : 'pointer' }}
            >
              <Typography variant="body1" color="secondary">
                {t('spending_category', { ns: 'transactions' })}
              </Typography>
              {spendingCategory ? (
                <Typography variant="h3">
                  {spendingCategory?.category} • {spendingCategory.name}
                </Typography>
              ) : (
                <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
              )}
            </Stack>
          </Stack>
        )}

        {/* Source Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={readOnly ? undefined : () => setOpenDialogSelectSourceWallets(true)}
            sx={{ cursor: readOnly ? 'default' : 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.lending_source_wallet', { ns: 'loan' })}
            </Typography>
            {sourceWallet?.wallet ? (
              <Typography variant="h3">
                {sourceWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Date */}
        <DatePickerComponent
          datePicker={datePicker}
          setDatePicker={(date: Dayjs | undefined) => {
            setDatePicker(date);
            setDatePickerProp(date);
          }}
          openDatePicker={openDatePicker}
          setOpenDatePicker={setOpenDatePicker}
          readOnly={readOnly}
          clearable={false}
        />

        {/* Description */}
        <DescriptionField
          description={description}
          setDescription={(value: string) => {
            setDescription(value);
            setDescriptionProp(value);
          }}
          ref={descriptionInputRef}
          readOnly={readOnly}
        />
      </Stack>

      <WalletSelector
        open={openDialogSelectSourceWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setSourceWallet(data);
          setSourceWalletProp(data);
          setOpenDialogSelectSourceWallets(false);
        }}
        walletTypes={['BASIC', 'CREDIT']}
        customTitle={t('transaction_form.lending_source_wallet', { ns: 'loan' })}
      />
      <WalletSelector
        open={openDialogSelectLendWallets}
        onClose={handleOnCloseDialog}
        multipleSelection
        onSelectWallets={(data: Wallet[]) => {
          setLendWallets(data);
          setOpenDialogSelectLendWallets(false);
        }}
        walletTypes={['LOAN']}
        personalWallet
        customTitle={t('transaction_form.lending_destination_wallet_title', { ns: 'loan', defaultValue: 'Bạn cho (những) ai vay?' })}
      />

      {/* ReAllocate Money Dialog */}
      <Dialog
        open={openReAllocateMoneyDialog}
        fullScreen
        onClose={() => setOpenReAllocateMoneyDialog(false)}
      >
        <ReAllocateMoney
          totalAmount={amount || 0}
          participants={lendWallets.map((wallet) => ({
            id: wallet.wallet.id,
            name: wallet.wallet.name,
            amount: allocateAmountMap.get(wallet.wallet.id) || 0,
            portions: allocatePortionsMap.get(wallet.wallet.id) || 1,
          }))}
          onClose={() => setOpenReAllocateMoneyDialog(false)}
          onComplete={handleReAllocateComplete}
        />
      </Dialog>
    </>
  );
};

export default LendTransactionForm;
